import kaplay from "kaplay";
// import "kaplay/global"; // uncomment if you want to use without the k. prefix

// GAME SETTINGS

const k = kaplay({
  width: 208,
  height: 128,
  stretch: true,
  letterbox: true,
  crisp: true,
  background: "1a1c2c",
  global: false,
  font: "monogram",
});

// GLOBALS

const TILE_WIDTH = 8;
const TILE_ROWS = 16;
const TILE_COLS = 16;
const BOARD_WIDTH = TILE_WIDTH * TILE_COLS;
const BOARD_HEIGHT = TILE_WIDTH * TILE_ROWS;
const UI_WIDTH = 39;
const BUTTONS_X = 12; // for relative placement of buttons
const BUTTONS_Y = 57; // ...
const BUTTONS_DX = 10; // ...
const BUTTONS_DY = 14; // ...
const BOARD_BG_COLOR = k.Color.fromHex("1D2B53");
const TEXT_COLOR = k.Color.fromHex("C2C3C7");
const P1_COLOR = k.Color.fromHex("FF004D");
const P2_COLOR = k.Color.fromHex("29ADFF");
const PELLET_COLOR = k.Color.fromHex("00E436");
const SPRITES = [
  {
    name: "snake",
    slice: [1, 5],
  },
  {
    name: "pellet",
    slice: [1, 1],
  },
];
const BUTTONS = [
  {
    name: "btn_a",
    pos: [BUTTONS_X - BUTTONS_DX, BUTTONS_Y],
    key: "a",
    action: {
      player: 0,
      move: "l",
    },
  },
  {
    name: "btn_d",
    pos: [BUTTONS_X + BUTTONS_DX, BUTTONS_Y],
    key: "d",
    action: {
      player: 0,
      move: "r",
    },
  },
  {
    name: "btn_w",
    pos: [BUTTONS_X, BUTTONS_Y - BUTTONS_DY],
    key: "w",
    action: {
      player: 0,
      move: "u",
    },
  },
  {
    name: "btn_s",
    pos: [BUTTONS_X, BUTTONS_Y + BUTTONS_DY],
    key: "s",
    action: {
      player: 0,
      move: "d",
    },
  },
  {
    name: "btn_left",
    pos: [UI_WIDTH + BOARD_WIDTH + BUTTONS_X - BUTTONS_DX, BUTTONS_Y],
    key: "left",
    action: {
      player: 1,
      move: "l",
    },
  },
  {
    name: "btn_right",
    pos: [UI_WIDTH + BOARD_WIDTH + BUTTONS_X + BUTTONS_DX, BUTTONS_Y],
    key: "right",
    action: {
      player: 1,
      move: "r",
    },
  },
  {
    name: "btn_up",
    pos: [UI_WIDTH + BOARD_WIDTH + BUTTONS_X, BUTTONS_Y - BUTTONS_DY],
    key: "up",
    action: {
      player: 1,
      move: "u",
    },
  },
  {
    name: "btn_down",
    pos: [UI_WIDTH + BOARD_WIDTH + BUTTONS_X, BUTTONS_Y + BUTTONS_DY],
    key: "down",
    action: {
      player: 1,
      move: "d",
    },
  },
];

// LOAD ASSETS

k.loadRoot("./"); // A good idea for Itch.io publishing later
SPRITES.forEach((sprite) => {
  k.loadSprite(sprite.name, `sprites/${sprite.name}.png`, {
    sliceX: sprite.slice[0],
    sliceY: sprite.slice[1],
  });
});
BUTTONS.forEach((btn) => {
  k.loadSprite(btn.name, `sprites/${btn.name}.png`, {
    sliceX: 1,
    sliceY: 2,
  });
});
k.loadBitmapFont("monogram", "fonts/monogram.png", 6, 12);

// HELPER FUNCTIONS

const reverseDir = {
  l: "r",
  r: "l",
  u: "d",
  d: "u",
};

function tileToPixel(tile) {
  return [(tile % 16) * 8 + UI_WIDTH, Math.floor(tile / 16) * 8];
}

function collidesWithSnake(tile, snakes) {
  for (const snake of snakes) {
    if (snake.body.includes(tile)) {
      return true;
    }
  }
  return false;
}

function collidesWithTail(tile, snakes) {
  for (const snake of snakes) {
    if (snake.body.slice(0, -1).includes(tile)) {
      return true;
    }
  }
  return false;
}

function isOrthogonal(newDir, oldDir) {
  return newDir !== oldDir && newDir !== reverseDir[oldDir];
}

function makeSnake(startPos, startDir, sprite, color) {
  let body = [startPos, startPos + 1, startPos + 2, startPos + 3];
  if (startDir === "l") {
    body.reverse();
  }
  return {
    body: body,
    bodyDirs: [startDir, startDir, startDir],
    currDir: startDir,
    nextDirs: [],
    growing: 0,
    sprite: sprite,
    color: color,
  };
}

function getNextTile(snake) {
  const head = snake.body.at(-1);

  // update current direction if input queue is not empty
  if (snake.nextDirs.length !== 0) {
    snake.currDir = snake.nextDirs.shift();
  }

  // find next tile
  let nextTile = -1;
  switch (snake.currDir) {
    case "l":
      nextTile = head - 1;
      if (head % TILE_COLS === 0) {
        nextTile += TILE_COLS;
      }
      break;
    case "r":
      nextTile = head + 1;
      if (nextTile % TILE_COLS === 0) {
        nextTile -= TILE_COLS;
      }
      break;
    case "u":
      nextTile = head - TILE_COLS;
      if (nextTile < 0) {
        nextTile += TILE_COLS * TILE_ROWS;
      }
      break;
    case "d":
      nextTile = head + TILE_COLS;
      if (nextTile >= TILE_COLS * TILE_ROWS) {
        nextTile -= TILE_COLS * TILE_ROWS;
      }
  }
  return nextTile;
}

function moveSnake(snake, nextTile) {
  // update body and body objects
  if (snake.growing !== 0) {
    snake.growing--;
  } else {
    snake.body.shift();
    snake.bodyDirs.shift();
  }
  snake.body.push(nextTile);
  snake.bodyDirs.push(snake.currDir);

  // return tile moved into
  return nextTile;
}

function addDir(snake, newDir) {
  let testDir = snake.currDir;
  if (snake.nextDirs.length !== 0) {
    testDir = snake.nextDirs.at(-1);
  }
  if (isOrthogonal(newDir, testDir)) {
    snake.nextDirs.push(newDir);
  }
}

// map directions input to array: [frame, flipX, flipY]
const frameInfo = {
  r: [0, false, false],
  l: [0, true, false],
  d: [1, false, false],
  u: [1, false, true],
  ru: [2, true, false],
  ur: [2, true, false],
  rd: [2, true, true],
  dr: [2, true, true],
  lu: [2, false, false],
  ul: [2, false, false],
  ld: [2, false, true],
  dl: [2, false, true],
  rl: [3, false, false],
  lr: [3, false, false],
  du: [4, false, false],
  ud: [4, false, false],
};

// get list of sprite frame numbers and flips from direction list
function getFrameInfo(bodyDirs) {
  let result = new Array(bodyDirs.length + 1).fill("");
  bodyDirs.forEach((dir, i) => {
    result[i] += dir;
    result[i + 1] += reverseDir[dir];
  });
  return result.map((dirs) => frameInfo[dirs]);
}

function drawSnake(snake) {
  const frameInfo = getFrameInfo(snake.bodyDirs);
  snake.body.forEach((tile, i) => {
    k.drawSprite({
      sprite: snake.sprite,
      pos: k.vec2(...tileToPixel(tile)),
      frame: frameInfo[i][0],
      flipX: frameInfo[i][1],
      flipY: frameInfo[i][2],
      color: snake.color,
    });
  });
}

function spawnPellet(snakes) {
  const tile = Math.floor(Math.random() * TILE_COLS * TILE_ROWS);
  if (collidesWithSnake(tile, snakes)) {
    return null;
  }
  return tile;
}

function drawPellet(tile) {
  k.drawSprite({
    sprite: "pellet",
    pos: k.vec2(...tileToPixel(tile)),
    color: PELLET_COLOR,
  });
}

function drawBoard() {
  k.drawRect({
    pos: k.vec2(UI_WIDTH, 0),
    width: BOARD_WIDTH,
    height: BOARD_HEIGHT,
    color: BOARD_BG_COLOR,
  });
}

// BUTTONS AND INPUT

function addControls(snakes) {
  BUTTONS.forEach((btn) => {
    const b = k.add([
      k.pos(...btn.pos),
      k.sprite(btn.name, {
        frame: 0,
      }),
    ]);
    k.onKeyPress(btn.key, () => {
      b.frame = 1;
      addDir(snakes[btn.action.player], btn.action.move);
    });
    k.onKeyRelease(btn.key, () => {
      b.frame = 0;
    });
    k.onTouchStart((pos) => {
      let x = pos.x - btn.pos[0];
      let y = pos.y - btn.pos[1];
      if (x > 0 && x < 15 && y > 0 && y < 15) {
        b.frame = 1;
        addDir(snakes[btn.action.player], btn.action.move);
      }
    });
    k.onTouchEnd(() => {
      b.frame = 0;
    });
  });
}

// SCENES

k.scene("game", () => {
  // spawn snakes
  const snakes = [
    makeSnake(82, "r", "snake", P1_COLOR),
    makeSnake(170, "l", "snake", P2_COLOR),
  ];
  // spawn pellet
  let pellet = spawnPellet(snakes);

  // main loop
  k.loop(0.2, () => {
    const moves = snakes.map((snake) => getNextTile(snake));

    const [coll1, coll2] = moves.map((tile) => collidesWithTail(tile, snakes));

    if ((coll1 && coll2) || moves[0] === moves[1]) {
      k.go("menu", "It's a draw!", TEXT_COLOR);
    } else if (coll1) {
      scores[1]++;
      k.go("menu", "Blue wins!", P2_COLOR);
    } else if (coll2) {
      scores[0]++;
      k.go("menu", "Red wins!", P1_COLOR);
    }

    [0, 1].forEach((i) => {
      moveSnake(snakes[i], moves[i]);
      if (moves[i] === pellet) {
        snakes[i].growing += 2;
        snakes[1 - i].growing++;
        pellet = null;
      }
    });

    if (pellet === null) {
      pellet = spawnPellet(snakes);
    }
  });

  k.onDraw(() => {
    drawBoard();
    drawSnake(snakes[0]);
    drawSnake(snakes[1]);
    if (pellet !== null) {
      drawPellet(pellet);
    }
  });

  addControls(snakes);
});

k.scene("menu", (titleText, titleColor) => {
  const snakes = [
    {
      body: [49, 33, 17, 18, 19],
      bodyDirs: ["u", "u", "r", "r"],
      sprite: "snake",
      color: P1_COLOR,
    },
    {
      body: [206, 222, 238, 237, 236],
      bodyDirs: ["d", "d", "l", "l"],
      sprite: "snake",
      color: P2_COLOR,
    },
  ];

  k.onKeyPress("space", () => {
    k.go("game");
  });
  k.onTouchStart((pos) => {
    if (
      pos.x > UI_WIDTH &&
      pos.x < UI_WIDTH + BOARD_WIDTH &&
      pos.y > 0 &&
      pos.y < BOARD_HEIGHT
    ) {
      k.go("game");
    }
  });

  k.onDraw(() => {
    drawBoard();
    k.drawText({
      text: titleText,
      size: 12,
      width: BOARD_WIDTH,
      align: "center",
      pos: k.vec2(UI_WIDTH, 32),
      color: titleColor,
    });
    k.drawText({
      text: "press SPACE or\ntouch to start",
      size: 12,
      width: BOARD_WIDTH,
      align: "center",
      pos: k.vec2(UI_WIDTH, 72),
      color: TEXT_COLOR,
    });
    k.drawText({
      text: `[p1]${scores[0]}[/p1] [mid]-[/mid] [p2]${scores[1]}[/p2]`,
      size: 12,
      pos: k.vec2(UI_WIDTH, 52),
      width: BOARD_WIDTH,
      align: "center",
      styles: {
        p1: {
          color: P1_COLOR,
        },
        p2: {
          color: P2_COLOR,
        },
        mid: {
          color: TEXT_COLOR,
        },
      },
    });
    snakes.forEach((snake) => {
      drawSnake(snake);
    });
  });
});

k.scene();

// INIT

let scores = [0, 0];

k.go("menu", "SnakeBattle", TEXT_COLOR);
